import React, { ReactNode, useEffect, useState } from 'react';
import cx from 'classnames';
import { ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Chapter, ReportEntity, WorkDayChapter, WorkDayProduct } from '@data';
import { getFullNameOrEmail, shortDate, toFixedHours } from '@app/utils';
import { Button } from '@ui/components/ui/button';

/**
 * Correcting a report from wherever it is read. Optional throughout: the client's view of a sprint
 * passes none of these and stays exactly as read-only as it is today, while our view passes all three
 * so a manager who spots a wrong number can fix it without leaving the sprint.
 */
export interface DayLogAuthoring {
  /** Absent where a day can be corrected but nothing new may be booked into it. */
  onAddForDay?: (workday: string) => void;
  onEditReport: (report: ReportEntity) => void;
  onRemoveReport: (report: ReportEntity) => void;
}

/** What a row's hours were worked between, for a log that knows. Only incident work does, where the clock decides the rate. */
export type ReportTimeResolver = (report: ReportEntity) => ReactNode;

// A person's entry: a full-width row (name left, hours right) separated by a hairline; expand for what
// they did. Follows the shared expand-all toggle via an effect so open/close animates.
const MemberRow = ({
  report,
  expandAll,
  authoring,
  timeFor,
}: {
  report: ReportEntity;
  expandAll: boolean;
  authoring?: DayLogAuthoring;
  timeFor?: ReportTimeResolver;
}): JSX.Element => {
  const { t } = useTranslation();
  const hasDetail = Boolean(report.today);
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(hasDetail && expandAll), [expandAll, hasDetail]);
  // The brackets say the figure is the length of that stretch, not a second one beside it.
  const time = timeFor?.(report);
  return (
    <div className="border-b border-border last:border-b-0">
      {/* The expander and the two actions are siblings rather than nested: a button inside a button is
          invalid, and the row is the expander. */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => hasDetail && setOpen(value => !value)}
          aria-expanded={open}
          className={cx(
            'flex min-w-0 flex-1 items-center justify-between gap-3 py-2.5 text-left text-sm',
            !hasDetail && 'cursor-default',
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <ChevronRight
              className={cx(
                'size-4 shrink-0 text-muted-foreground transition-transform duration-300',
                open && 'rotate-90',
                !hasDetail && 'invisible',
              )}
            />
            <span className="truncate font-medium text-foreground">
              {getFullNameOrEmail(report.involvement.membership.user)}
            </span>
          </span>
          <span className="flex shrink-0 items-baseline gap-1.5 tabular-nums text-muted-foreground">
            {time}
            <span>
              {time ? <>({toFixedHours(report.minutes / 60)}h)</> : <>{toFixedHours(report.minutes / 60)}h</>}
            </span>
          </span>
        </button>
        {authoring && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('a11y.editReport')}
              className="size-8 shrink-0 focus:outline-none"
              onClick={() => authoring.onEditReport(report)}
            >
              <Pencil className="size-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('a11y.deleteReport')}
              className="size-8 shrink-0 focus:outline-none"
              onClick={() => authoring.onRemoveReport(report)}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          </>
        )}
      </div>
      {hasDetail && (
        <div
          className={cx(
            'grid transition-[grid-template-rows] duration-300 ease-out',
            open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="overflow-hidden">
            <div className="mb-2.5 whitespace-pre-wrap pl-6 pr-24 text-sm text-muted-foreground">{report.today}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// A role within a day: its own white card (so roles read separately), with an orange heading and its
// people as divider-separated rows (so each person lines up with their hours).
const RoleSection = ({
  chapter,
  chapters,
  expandAll,
  authoring,
  timeFor,
}: {
  chapter: WorkDayChapter;
  chapters?: Chapter[];
  expandAll: boolean;
  authoring?: DayLogAuthoring;
  timeFor?: ReportTimeResolver;
}): JSX.Element => {
  const label = chapters?.find(c => c.role === chapter.name)?.text ?? chapter.name;
  return (
    <div className="mt-3 rounded-lg bg-card px-4 pb-1.5 pt-3 first:mt-2">
      <h4 className="mb-0.5 text-sm font-semibold text-primary">{label}</h4>
      {chapter.reports.map(report => (
        <MemberRow key={report.id} report={report} expandAll={expandAll} authoring={authoring} timeFor={timeFor} />
      ))}
    </div>
  );
};

/**
 * A day in the sprint: a full-width card holding its roles and people as a lined list.
 *
 * The prototype prices each row and each day total from a `rateFor` resolver. Money is out under the
 * financial quarantine; where it goes back is the right-hand end of each row.
 */
const DayCard = ({
  day,
  chapters,
  expandAll,
  authoring,
  timeFor,
}: {
  day: WorkDayProduct;
  chapters?: Chapter[];
  expandAll: boolean;
  authoring?: DayLogAuthoring;
  timeFor?: ReportTimeResolver;
}): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(expandAll), [expandAll]);
  return (
    <div className="mt-4 rounded-lg bg-background px-4 py-4 first:mt-0">
      <div className="flex items-center gap-1 pr-4">
        <button
          type="button"
          onClick={() => setOpen(value => !value)}
          aria-expanded={open}
          // The row is the expander; the add action is its sibling so the two buttons do not nest.
          className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
        >
          <span className="flex items-center gap-2">
            <ChevronRight
              className={cx(
                'size-4 shrink-0 text-muted-foreground transition-transform duration-300',
                open && 'rotate-90',
              )}
            />
            {/* Day headers share the canonical 'eee, dd MMMM' format from date-utils. */}
            <h3 className="text-base font-semibold text-foreground">{shortDate(day.date)}</h3>
          </span>
          <span className="text-base font-semibold tabular-nums text-foreground">
            {toFixedHours(day.totalMinutes / 60)}h
          </span>
        </button>
        {authoring?.onAddForDay && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('titles.addEntry')}
            className="size-8 shrink-0 focus:outline-none"
            onClick={() => authoring.onAddForDay?.(day.date)}
          >
            <Plus className="size-4" aria-hidden />
          </Button>
        )}
      </div>
      <div
        className={cx(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="mt-2">
            {day.chapters
              .filter(chapter => chapter.totalMinutes > 0)
              .map(chapter => (
                <RoleSection
                  key={chapter.name}
                  chapter={chapter}
                  chapters={chapters}
                  expandAll={expandAll}
                  authoring={authoring}
                  timeFor={timeFor}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The sprint read by day, earliest first.
 *
 * `expandAll` is the caller's to own, for the same reason as `RoleSummary`: it has to hold its position
 * when the reader switches between the two views.
 */
const DayLog = ({
  days,
  chapters,
  expandAll,
  authoring,
  timeFor,
}: {
  days: WorkDayProduct[];
  chapters?: Chapter[];
  expandAll: boolean;
  authoring?: DayLogAuthoring;
  timeFor?: ReportTimeResolver;
}): JSX.Element => (
  <div>
    {days.map(day => (
      <DayCard
        key={day.date}
        day={day}
        chapters={chapters}
        expandAll={expandAll}
        authoring={authoring}
        timeFor={timeFor}
      />
    ))}
  </div>
);

export default DayLog;
